
import { useState, useCallback, useRef } from 'react';
import { AdminAction } from '@/types/adminTypes';
import { supabase } from '@/lib/supabase';
import { getClientInfo } from '@/utils/clientInfoUtils';
import { recordAdminAction as recordAction } from '@/services/adminActionService';

/**
 * Determines if an action should be tracked locally in the UI
 * More actions can be shown in UI than stored in database
 */
const shouldTrackLocally = (actionType: AdminAction['actionType'], details?: string): boolean => {
  // Skip trivial "other" actions for local tracking
  if (actionType === 'other' && details) {
    // Filter out common noise
    if (details.includes('Quét mã QR không hợp lệ') || 
        details.includes('Lỗi quét mã QR') ||
        details.includes('Nhập mã không hợp lệ')) {
      return false;
    }
  }
  
  // Track everything else locally
  return true;
};

/**
 * Hook for managing admin actions - directly using Supabase
 */
export const useAdminActions = () => {
  const [adminActions, setAdminActions] = useState<AdminAction[]>([]);
  const [isThrottled, setIsThrottled] = useState(false);
  
  // Add caching and tracking to reduce duplicate loads
  const lastLoadTimestamp = useRef<number>(0);
  const MIN_LOAD_INTERVAL = 30000; // 30 seconds minimum between loads
  const initialLoadPerformed = useRef(false);
  
  // Load admin actions with caching
  const loadAdminActions = useCallback(async () => {
    try {
      const now = Date.now();
      
      // Skip if loaded recently and not the first load
      if (now - lastLoadTimestamp.current < MIN_LOAD_INTERVAL && initialLoadPerformed.current) {
        console.log('Skipping admin actions load - too recent');
        return adminActions;
      }
      
      console.log('Loading admin actions from Supabase');
      lastLoadTimestamp.current = now;
      initialLoadPerformed.current = true;
      
      const { data, error } = await supabase
        .from('admin_actions')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(500);
      
      if (error) {
        console.error('Error loading admin actions:', error);
        return adminActions;
      }
      
      setAdminActions(data || []);
      return data || [];
    } catch (error) {
      console.error('Error in loadAdminActions:', error);
      return adminActions;
    }
  }, [adminActions]);
  
  // Record a new admin action
  const recordAdminAction = useCallback(
    async (
      actionType: AdminAction['actionType'],
      adminName: string,
      details?: string,
      attendeeCode?: string
    ) => {
      try {
        // Reset throttle state at the beginning of each attempt
        setIsThrottled(false);
        
        // Call the service function to record the action to the database (with filtering)
        const { newAction, updatedActions } = await recordAction(
          actionType,
          adminName,
          details,
          attendeeCode
        );
        
        // Check if we should track this action locally in the UI
        if (shouldTrackLocally(actionType, details)) {
          // Update local state with the new action
          const newLocalActions = [newAction, ...adminActions].slice(0, 500);
          setAdminActions(newLocalActions);
        }
        
        return { newAction, updatedActions };
      } catch (error) {
        console.error('Error in recordAdminAction:', error);
        
        // Check if the error is due to throttling
        if (error instanceof Error && error.message.includes('throttled')) {
          setIsThrottled(true);
          return { error: 'throttled' };
        }
        
        // Create a new action manually if service fails
        const now = new Date().toISOString();
        const clientInfo = getClientInfo();
        
        const newAction: AdminAction = {
          actionId: crypto.randomUUID(),
          actionType,
          performedBy: adminName,
          timestamp: now,
          details,
          attendeeCode,
          clientInfo
        };
        
        // Only try to insert directly if it's an important action
        if (actionType !== 'other' || 
            (details && details.includes('Admin page accessed'))) {
          try {
            await supabase.from('admin_actions').insert([newAction]);
          } catch (insertError) {
            console.error('Direct insert fallback failed:', insertError);
          }
        }
        
        // Only update local state if we should track this action
        if (shouldTrackLocally(actionType, details)) {
          const updatedActions = [newAction, ...adminActions].slice(0, 500);
          setAdminActions(updatedActions);
        }
        
        return { newAction, updatedActions: [newAction, ...adminActions].slice(0, 500) };
      }
    },
    [adminActions]
  );
  
  return {
    adminActions,
    loadAdminActions,
    recordAdminAction,
    isThrottled,
    usingSupabase: true // Always true now
  };
};
